"use client";

import * as React from "react";
import { BookOpen, Clock, Monitor, Zap, MessageCircle, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
const courseModules = [{
  title: "Anatomía Funcional y Biomecánica",
  content: "Estudio profundo del sistema musculoesquelético y análisis de las cadenas cinéticas aplicadas al gesto deportivo para prevenir lesiones y mejorar el rendimiento físico."
}, {
  title: "Fisiopatología de las Lesiones",
  content: "Análisis de los mecanismos de lesión, procesos de reparación tisular y abordaje de patologías frecuentes como esguinces, desgarros, tendinopatías y sobrecargas."
}, {
  title: "Masoterapia y Técnicas Manuales",
  content: "Desarrollo de habilidades en masaje deportivo (pre, intra y post competencia), técnicas de liberación miofascial y estiramientos para optimizar la recuperación muscular."
}, {
  title: "Kinesiotaping y Vendaje Funcional",
  content: "Aplicación práctica de vendajes neuromusculares y funcionales para brindar soporte articular, estabilidad y asistencia muscular durante la práctica deportiva."
}, {
  title: "Rehabilitación y Readaptación Física",
  content: "Planificación de protocolos de ejercicios terapéuticos y progresión de cargas para una vuelta segura al campo de juego (Return to Play) tras una lesión."
}];
export default function DetallesDelCurso({
  isBusinessCourse = false
}: {
  isBusinessCourse?: boolean;
}) {
  return <section id="detalles-del-curso" className="relative bg-background py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[hsl(222_47%_11%)]">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(to_bottom,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:32px_32px]"></div>
        {}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)]"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          
          {}
          <div className="lg:col-span-7 space-y-8">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-4xl font-sans font-bold text-white tracking-tight">
                Programa de Estudio
              </h2>
              <p className="text-white/70 text-lg max-w-2xl">
                Explora los módulos detallados que componen nuestra formación técnica, diseñados para llevarte desde los conceptos básicos hasta la práctica profesional.
              </p>
            </div>

            <div className="bg-[hsla(222,47%,15%,0.7)] backdrop-blur-[12px] rounded-3xl p-6 md:p-8 border border-white/10 shadow-2xl">
              <Accordion type="single" collapsible className="w-full">
                {courseModules.map((module, i) => <AccordionItem key={i} value={`module-${i}`} data-index={i} className="border-white/10">
                    <AccordionTrigger className="text-white hover:text-white/90 hover:no-underline py-5 group">
                      <div className="flex items-center gap-4 text-left">
                        <div className="bg-secondary/10 p-2 rounded-lg group-hover:bg-secondary/20 transition-colors">
                          <Zap className="size-5 text-secondary shrink-0" />
                        </div>
                        <span className="font-sans font-bold text-lg md:text-xl">
                          {module.title}
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-white/70 text-base leading-relaxed pb-6 pl-[3.25rem] md:pl-[3.75rem]">
                      {module.content}
                    </AccordionContent>
                  </AccordionItem>)}
              </Accordion>
            </div>
          </div>

          {}
          <div className="lg:col-span-5 lg:sticky lg:top-24">
            <Card className="rounded-[24px] border-border/50 shadow-2xl overflow-hidden bg-card/95 backdrop-blur-sm">
              <CardHeader className="bg-muted/30 pb-8 pt-8 border-b border-border/50">
                <CardTitle className="text-2xl font-sans font-bold">
                  Detalles de Inscripción
                </CardTitle>
                <CardDescription className="text-base">
                  Información clave para comenzar tu formación técnica.
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-8 space-y-8">
                <div className="space-y-6 font-mono text-sm">
                  <div className="flex items-center justify-between border-b border-border/50 pb-4">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <Clock className="size-5 text-primary" />
                      <span>Duración</span>
                    </div>
                    <span className="font-semibold text-foreground">12 Semanas</span>
                  </div>
                  
                  <div className="flex items-center justify-between border-b border-border/50 pb-4">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <Monitor className="size-5 text-primary" />
                      <span>Modalidad</span>
                    </div>
                    <span className="font-semibold text-foreground">
                      {isBusinessCourse ? "100% Online" : "100% Presencial"}
                    </span>
                  </div>

                  <div className="flex items-center justify-between border-b border-border/50 pb-4">
                    <div className="flex items-center gap-3 text-muted-foreground">
                      <BookOpen className="size-5 text-primary" />
                      <span>Certificación</span>
                    </div>
                    <span className="font-semibold text-foreground">Incluida</span>
                  </div>
                </div>

                <div className="pt-2">
                  <div className="text-sm font-mono text-muted-foreground mb-2 uppercase tracking-wider">
                    Inversión Única
                  </div>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold tracking-tighter text-foreground">$21.000</span>
                    <span className="text-xl text-muted-foreground font-mono font-medium">
                      $U
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 font-mono">
                    *Opciones de pago en cuotas disponibles.
                  </p>
                </div>
              </CardContent>
              
              <CardFooter className="p-8 pt-0 flex flex-col gap-4">
                <Link to="https://mpago.la/your-link" className="w-full" newTab>
                  <Button size="lg" className="w-full group text-base font-semibold h-12 bg-[#009EE3] hover:bg-[#008ED2] text-white border-none shadow-md">
                    Pagar con Mercado Pago
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="https://wa.me/59896344395" className="w-full" newTab>
                  <Button size="lg" className="w-full group text-base font-semibold h-12 bg-[#25D366] hover:bg-[#22c35e] text-white border-none shadow-md">
                    <MessageCircle className="w-5 h-5 mr-2" />
                    Consultar por WhatsApp
                  </Button>
                </Link>
                <Button asChild size="lg" className="w-full text-base font-semibold h-12 bg-secondary text-secondary-foreground hover:bg-secondary/90 shadow-md border-none">
                  <Link to="https://ecap.edu.uy/kinesiologia-deportiva/">
                    ver programa
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}